import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@auth/AuthProvider';
import EventsController, { Event } from '@data/controller/EventsController';

export default function useEvents(providedChurchId?: string) {
  const auth = useAuth();
  const churchId = providedChurchId || auth.churchId;

  const [events, setEvents] = useState<Event[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!churchId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await EventsController.fetchEvents(churchId, auth);
      if (response.error) {
        setError(response.error);
        setEvents(null);
      } else if (response.body) {
        setEvents(response.body.events);
      } else {
        setEvents(null);
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load events');
      setEvents(null);
    } finally {
      setLoading(false);
    }
  }, [churchId, auth]);

  useEffect(() => {
    if (!churchId) return;

    let isMounted = true;

    const performFetch = async () => {
      await fetch();
      if (!isMounted) return;
    };

    performFetch();

    return () => {
      isMounted = false;
    };
  }, [churchId, fetch]);

  return { events, loading, error, refetch: fetch } as const;
}
